import { keccak256, toHex, type Address } from 'viem';
import { z } from 'zod';
import { hashPreparedCalls } from './calls.js';
import { prepareExperimentalConditionalRegistration, prepareExperimentalLinkedRegistration } from './conditionalOrderRegistration.js';

const decimalPattern = /^(0|[1-9][0-9]{0,77})$/;
const uint = (bits: number, positive = false) => z.string().regex(decimalPattern).refine(value =>
  decimalPattern.test(value) && BigInt(value) >= (positive ? 1n : 0n) && BigInt(value) < (1n << BigInt(bits)));
const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/).refine(value => !/^0x0{40}$/i.test(value))
  .transform(value => value.toLowerCase() as Address);
const hash = z.string().regex(/^0x[0-9a-fA-F]{64}$/).transform(value => value.toLowerCase());
const leg = z.strictObject({ minimumTotalOutput: uint(128, true), maximumFeeBps: z.number().int().min(0).max(10000),
  expiry: uint(256), trigger: z.strictObject({ threshold: uint(256, true), atOrBelow: z.boolean(), latchAfterFirstFill: z.boolean() }) });
const terms = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('independent'), buy: z.boolean(), maximumInput: uint(128, true), leg }),
  z.strictObject({ kind: z.literal('linked'), maximumInput: uint(128, true), first: leg, second: leg }),
]);
const schema = z.strictObject({ schemaVersion: z.literal(1), registrationId: z.uuid(), userId: z.string().min(1).max(128),
  version: z.enum(['conditional-trigger-order.1', 'conditional-linked-allocation.1']), chainId: z.literal(4663),
  engine: address, vault: address, owner: address, engineCodeHash: hash, configurationHash: hash,
  source: z.strictObject({ blockNumber: uint(256), blockHash: hash, observedAt: z.iso.datetime() }), terms });
export type ConditionalRegistrationIntent = z.input<typeof schema>;

/** Immutable, JSON-safe owner intent BEFORE chain-assigned IDs or keeper fills.
 * Server must independently bind identity/profile/source and obtain owner approval.
 * No signature, nonce reservation, submission outcome or chain assignment is stored
 * here. Old intents remain readable; execution must recheck expiry and authority.
 */
export function readConditionalRegistrationIntent(input: unknown) {
  const record = schema.parse(input);
  if (record.terms.kind === 'linked' && record.version !== 'conditional-linked-allocation.1')
    throw new Error('Linked registration requires linked engine version');
  const common = { version: record.version, chainId: record.chainId, engine: record.engine,
    maximumInput: BigInt(record.terms.maximumInput) };
  const decodeLeg = (value: z.infer<typeof leg>) => ({ minimumTotalOutput: BigInt(value.minimumTotalOutput),
    maximumFeeBps: value.maximumFeeBps, expiry: BigInt(value.expiry),
    trigger: { ...value.trigger, threshold: BigInt(value.trigger.threshold) } });
  const prepared = record.terms.kind === 'independent'
    ? prepareExperimentalConditionalRegistration({ ...common, buy: record.terms.buy, ...decodeLeg(record.terms.leg) })
    : prepareExperimentalLinkedRegistration({ ...common, version: 'conditional-linked-allocation.1',
      first: decodeLeg(record.terms.first), second: decodeLeg(record.terms.second) });
  const recordJson = JSON.stringify(record);
  const call = { to: prepared.call.target, value: prepared.call.value, data: prepared.call.data };
  return { record, recordJson, intentHash: keccak256(toHex(recordJson)), call, callsHash: hashPreparedCalls([call]),
    executionAuthorized: false as const, registrationProven: false as const,
    verification: 'independent-owner-profile-source-submission-and-canonical-assignment-required' as const };
}
